<template>
  <v-row>
    <v-col cols="12" sm="12">
      <div class="d-flex align-center justify-space-between">
        <h2 class="text--primary text-lg font-weight-medium">
          {{ $t("Palautekanava") }}
        </h2>

      </div>
    </v-col>
    <v-col cols="12 sm=12">
      <v-alert text color="info">
        {{ $t('feedback_newview_info') }}
      </v-alert>
      <v-btn color="primary" @click="$router.push({ name: 'admin-feedback-dashboard' })">
        {{ $t('Uusi näkymä') }}
      </v-btn>
    </v-col>
    <v-col cols="12" sm="12">
      <v-card class="
          max-h-content-container
          app-email
          h-full
          position-relative
          overflow-hidden
          text-sm
        ">
        <v-navigation-drawer v-model="isLeftSidebarOpen" width="260" mobile-breakpoint="sm" absolute
          :right="$vuetify.rtl" touchless :temporary="$vuetify.breakpoint.smAndDown">
          <p class="mt-8 mx-5 mb-1">{{ $t("SUODATA") }}</p>
          <v-list dense>
            <v-list-item-group v-model="statusFilter" @change="filterChanged">
              <v-list-item v-for="status in statusLabels" :key="status.value" :value="status.value">
                <v-list-item-icon class="me-2">
                  <v-badge :color="status.color" dot inline class="align-self-center label-dot"></v-badge>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-base">
                    {{ status.title }} ({{
                      getFeedbackCountByStatus(status.value)
                    }})
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-navigation-drawer>

        <div class="email-content-area h-full position-relative"
          :style="{ 'margin-left': isLeftSidebarOpen ? '260px' : '0' }">
          <!-- 1st Row -->
          <div class="d-flex align-center px-4">
            <v-icon @click="isLeftSidebarOpen = !isLeftSidebarOpen">
              {{ icons.mdiMenu }}
            </v-icon>
            <v-text-field style="visibility: hidden" hide-details></v-text-field>
            <v-divider></v-divider>
          </div>

          <!-- 2nd Row -->
          <div class="h-full">
            <perfect-scrollbar :settings="perfectScrollbarSettings" class="ps-email-view-content">
              <v-list width="100%">
                <div v-for="feedback in filteredFeedbacks" :key="`feedback${feedback.id}`">
                  <v-list-item two-line @click="openFeedback(feedback)">
                    <v-list-item-icon>
                      <v-icon size="18" :color="getStatusColor(feedback.status)">
                        {{ icons.mdiCircle }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <div>
                          {{ getDateTime(feedback.insert_date) }}
                        </div>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{ getAge(feedback) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="feedback.category">
                      <v-chip>{{ feedback.category }}</v-chip>
                    </v-list-item-action>
                    <v-list-item-action>
                      <v-tooltip left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on">{{ icons.mdiEye }}</v-icon>
                        </template>
                        <span>{{ feedback.preview }}</span>
                      </v-tooltip>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider></v-divider>
                </div>
              </v-list>
            </perfect-scrollbar>

            <!-- Email Details -->
            <v-navigation-drawer v-model="isEmailDetailsOpen" :right="!$vuetify.rtl" absolute mobile-breakpoint="sm"
              width="100%" class="email-view-navigation-drawer">
              <feedback-form v-if="viewFeedback" @close="isEmailDetailsOpen = false" :feedback="viewFeedback"
                @status-changed="refresh"></feedback-form>
            </v-navigation-drawer>
          </div>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { useTranslations } from "@/@core/libs/i18n";
import { computed, onMounted, ref } from "@vue/composition-api";
import adminApi from "@/api/admin";
import {
  mdiEye,
  mdiCircle,
  mdiMenu,
  mdiMagnify,
  mdiChevronRight,
  mdiChevronLeft,
  mdiTrashCanOutline,
  mdiStarOutline,
  mdiStar,
  mdiReload,
  mdiDotsVertical,
  mdiFolderOutline,
  mdiEmailOutline,
  mdiEmailOpenOutline,
  mdiLabelOutline,
  mdiAttachment,
  mdiAlertOctagonOutline,
} from "@mdi/js";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import feedbackForm from "./feedback_form.vue";
import moment from "moment";
import { i18n } from "@/plugins/i18n";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";

export default {
  components: {
    feedbackForm,
    PerfectScrollbar,
  },
  setup() {
    const feedbacks = ref([]);
    const statusFilter = ref(1);

    const isLeftSidebarOpen = ref(true);
    const isEmailDetailsOpen = ref(false);
    const viewFeedback = ref(null);

    const statusLabels = [
      {
        title: i18n.t("Saapuneet"),
        color: "error",
        value: 1,
      },
      {
        title: i18n.t("Käsittely kesken"),
        color: "warning",
        value: 2,
      },
      {
        title: i18n.t("Valmiit"),
        color: "success",
        value: 3,
      },
    ];

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const fetchFeedbacks = async () => {
      const response = await adminApi.getFeedbacks();

      if (response.ok) {
        feedbacks.value = response.data;
      } else {
        alert(response.error);
      }
    };

    const changeStatus = async (filterStatus) => {
      statusFilter.value = filterStatus;
    };

    const filteredFeedbacks = computed(() => {
      const filter = statusFilter.value ?? 1;
      return feedbacks.value.filter((a) => a.status == filter);
    });

    const filterChanged = () => {
      viewFeedback.value = null;
      isEmailDetailsOpen.value = false;
    };

    const fetchFeedback = async (id) => {
      const res = await adminApi.getFeedback(id);
      if (res.ok) {
        viewFeedback.value = res.data;
        isEmailDetailsOpen.value = true;
      }
    }

    const openFeedback = async (feedback) => {

      fetchFeedback(feedback.id);

    };

    const getAge = (feedback) => {
      const insert = moment(feedback.insert_date);
      const now = moment();
      const days = now.diff(insert, "days") + 1;
      const today = now.isSame(insert, "day");

      if (today) return i18n.t("Saapunut tänään");

      return i18n.t("Saapunut") + " " + days + " " + i18n.t("päivää sitten");
    };

    const getFeedbackCountByStatus = (status) => {
      return feedbacks.value.filter((a) => a.status == status).length;
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 1:
          return "error";
        case 2:
          return "warning";
        case 3:
          return "success";
        default:
          return "error";
      }
    };

    onMounted(() => {
      fetchFeedbacks();
    });

    const refresh = async () => {
      fetchFeedbacks();
    }

    return {
      feedbacks,
      statusLabels,
      isLeftSidebarOpen,
      isEmailDetailsOpen,
      filterChanged,
      changeStatus,
      filteredFeedbacks,
      openFeedback,
      getStatusColor,
      statusFilter,
      viewFeedback,
      getAge,
      refresh,
      getFeedbackCountByStatus,
      getDateTime,
      perfectScrollbarSettings,
      icons: {
        mdiCircle,
        mdiMenu,
        mdiMagnify,
        mdiChevronRight,
        mdiEye,
        mdiChevronLeft,
        mdiTrashCanOutline,
        mdiReload,
        mdiDotsVertical,
        mdiStarOutline,
        mdiStar,
        mdiFolderOutline,
        mdiEmailOutline,
        mdiEmailOpenOutline,
        mdiLabelOutline,
        mdiAttachment,
        mdiAlertOctagonOutline,
      },
    };
  },
};
</script>

<style></style>
